import { FunctionComponent } from "react";
import SaveIcon from "@mui/icons-material/Save";
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  MenuItem,
  CardHeader,
  SvgIcon,
  IconButton,
  Skeleton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  CustomBreadCrumbs,
  PageHeader,
  CustomInput,
  CustomSelect,
  Button,
} from "../../components";
import { CustomTextArea } from "../../components/CustomTextArea";
import { Icon } from "@mdi/react";
import { mdiHome, mdiDownload } from "@mdi/js";
import { useRefundDetailForm } from "./hook";
import { CustomSnackbar } from "../CustomSnackbar";
import { FormSection } from "../FormSection";
import { ViewChangesDialog } from "../ViewChangesDialog";
import { RefundHistory } from "../ViewChangesDialog/types";

type FileCardProps = {
  filename: string;
  url: string;
  docDate: Date;
  onOpenFile: () => void;
  onDownload: () => void;
};

const FileSkeleton = (): JSX.Element => {
  return (
    <Card
      sx={{
        maxWidth: 345,
        backgroundColor: "#FBFCFE",
        boxShadow: 3,
        borderRadius: 2,
        mt: 2,
        p: 2,
      }}
    >
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton width="100%" height={194} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
    </Card>
  );
};

const FileCard: FunctionComponent<FileCardProps> = (props): JSX.Element => {
  const month: string = props.docDate.toLocaleString("en-US", {
    month: "long",
  });
  const addedOn: string = `${props.docDate.getDay()} ${month} ${props.docDate.getFullYear()}`;

  return (
    <Card
      sx={{
        maxWidth: 345,
        backgroundColor: "#FBFCFE",
        boxShadow: 3,
        borderRadius: 2,
        mt: 2,
      }}
    >
      <CardHeader
        onClick={props.onOpenFile}
        title={
          <Typography
            fontSize={16}
            sx={{ color: "#1BA0E9", fontWeight: 600, cursor: "pointer" }}
          >
            {props.filename}
          </Typography>
        }
      />
      <CardContent sx={{ padding: 0 }}></CardContent>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pr={2}
      >
        <CardHeader
          title={
            <Typography
              variant="h6"
              fontWeight={600}
              fontSize={14}
              sx={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Lato" }}
            >
              Added on {addedOn}
            </Typography>
          }
        />
        <IconButton
          onClick={props.onDownload}
          color="default"
          sx={{ "&:hover": { color: "#1BA0E9" } }}
        >
          <SvgIcon>
            <Icon path={mdiDownload} />
          </SvgIcon>
        </IconButton>
      </Box>
    </Card>
  );
};

export const RefundDetailForm: FunctionComponent = (): JSX.Element => {
  const {
    isOpenViewChangesDialog,
    onChange,
    onChangeSelect,
    openFile,
    onDownload,
    statusDropdownLabel,
    onSave,
    onCancel,
    onCloseSnackbar,
    state,
    snackbarState,
    link,
    reasonItems,
    statusItems,
    pageTitle,
    isLoading,
    nonBillReasons,
    rejectReasons,
    onCloseViewChangesDialog,
    onOpenViewChangesDialog,
    refundId,
  } = useRefundDetailForm();

  let items: { id: string; description: string }[] = [];

  if (state.refundStatus === 2) {
    items = nonBillReasons;
  } else if (state.refundStatus === 3) {
    items = rejectReasons;
  }

  return (
    <Box>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress />
      </Backdrop>
      <CustomBreadCrumbs icon={mdiHome} path="Home/Refund" />
      <PageHeader title={pageTitle} />
      <Box mt={4}>
        <Card sx={{ boxShadow: 2, mt: 2, borderRadius: 4 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item>
                <CustomSelect
                  label="Refund Status"
                  name="refundStatus"
                  value={state.refundStatus}
                  onChange={onChangeSelect}
                >
                  {statusItems.map((value, index) => {
                    return (
                      <MenuItem value={value.id}>{value.description}</MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
              {(state.refundStatus === 2 || state.refundStatus === 3) && (
                <Grid item>
                  <CustomSelect
                    label={statusDropdownLabel()}
                    name="statusReason"
                    value={state.statusReason}
                    onChange={onChangeSelect}
                  >
                    {items.map((val) => {
                      return (
                        <MenuItem value={parseInt(val.id)}>
                          {val.description}
                        </MenuItem>
                      );
                    })}
                  </CustomSelect>
                </Grid>
              )}
            </Grid>
            {(state.refundStatus === 2 || state.refundStatus === 3) && (
              <Grid item mt={4}>
                <CustomTextArea
                  name="reviewNotes"
                  value={state.reviewNotes}
                  onChange={onChange}
                  width="40%"
                  label={{ text: "Review notes" }}
                  minLength={0}
                  maxLength={150}
                />
              </Grid>
            )}
            <FormSection title="Customer Info">
              <Grid item>
                <CustomInput
                  readOnly
                  name="email"
                  value={state.callId}
                  label={{ text: "Email" }}
                  onChange={onChange}
                />
              </Grid>
              <Grid item>
                <CustomInput
                  readOnly
                  name="firstname"
                  value={state.firstname}
                  label={{ text: "Firstname" }}
                  onChange={onChange}
                />
              </Grid>
              <Grid item>
                <CustomInput
                  readOnly
                  name="lastname"
                  value={state.lastname}
                  label={{ text: "Lastnaame" }}
                  onChange={onChange}
                />
              </Grid>
              <Grid item>
                <CustomInput
                  readOnly
                  name="phone"
                  value={state.phone}
                  label={{ text: "Phone" }}
                  onChange={onChange}
                />
              </Grid>
            </FormSection>
            <FormSection title="Refund Information">
              <Grid item>
                <CustomSelect
                  readOnly
                  name="reason"
                  value={state.reason}
                  label="Reason"
                  onChange={onChangeSelect}
                >
                  {reasonItems.map((value, index) => {
                    return (
                      <MenuItem value={value.id} key={index}>
                        {value.description}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
            </FormSection>
            <Box mt={2}>
              <CustomTextArea
                readOnly
                name="notes"
                value={state.notes}
                onChange={onChange}
                width="40%"
                label={{ text: "Notes" }}
                minLength={0}
                maxLength={150}
              />
            </Box>
            <FormSection title="Refund Documents">
              {isLoading ? (
                <FileSkeleton />
              ) : (
                <FileCard
                  onDownload={onDownload}
                  onOpenFile={openFile}
                  filename={link.filename}
                  docDate={new Date(link.docDate)}
                  url={link.url}
                />
              )}
            </FormSection>
            <Box
              mt={4}
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Button
                  height={40}
                  label="View Changes"
                  onClick={onOpenViewChangesDialog}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  startIcon={<SaveIcon />}
                  height={40}
                  label="Save"
                  onClick={onSave}
                />
                <Button height={40} label="Cancel" onClick={onCancel} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <ViewChangesDialog<RefundHistory>
        entity="REFUND"
        id={refundId!}
        label="Refund"
        isOpen={isOpenViewChangesDialog}
        onClose={onCloseViewChangesDialog}
      />
      <CustomSnackbar
        level={snackbarState.level as any}
        message={snackbarState.message}
        variant="secondary"
        open={snackbarState.isOpen}
        onClose={onCloseSnackbar}
      />
    </Box>
  );
};
